import React from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { 
  Summarize as SummarizeIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  CompareArrows as CompareArrowsIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  WarningAmber as WarningIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

// Map section titles to icons (all use consistent purple theme)
const SECTION_CONFIG = {
  'Executive Summary': { icon: <SummarizeIcon /> },
  'Population Trends': { icon: <TrendingUpIcon /> },
  'Enrollment Context': { icon: <SchoolIcon /> },
  'Competitive Landscape': { icon: <CompareArrowsIcon /> },
  'Priority Competitors': { icon: <CompareArrowsIcon /> },
  'Market Share Analysis': { icon: <AssessmentIcon /> },
  'Market Share & Population Context': { icon: <AssessmentIcon /> },
  'Demographic Positioning': { icon: <PeopleIcon /> },
  'Competitive Threats & Opportunities': { icon: <WarningIcon /> },
  'Overall Trajectory': { icon: <TrendingUpIcon /> },
  'Grade-Level Analysis': { icon: <AssessmentIcon /> },
  'Planning Implications': { icon: <SchoolIcon /> },
  // Combined/Overview summary sections
  'Key Takeaways': { icon: <AssessmentIcon /> },
  'Strategic Priorities': { icon: <TrendingUpIcon /> }
};

// Consistent purple theme for all sections
const THEME_COLOR = '#673ab7';
const THEME_BGCOLOR = '#ffffff';

// Helper function to get explorer link based on summary type
const getExplorerLink = (summaryType, schoolUuid) => {
  if (!schoolUuid) return null;
  
  if (summaryType === 'geographic') {
    return `/geographic-explorer?school=${schoolUuid}`;
  } else if (summaryType === 'competitive') {
    return `/competitive-explorer?school=${schoolUuid}`;
  } else if (summaryType === 'projections') {
    return `/projections-explorer?school=${schoolUuid}`;
  }
  return null;
};

// Helper function to get explorer display name
const getExplorerName = (summaryType) => {
  if (summaryType === 'geographic') return 'Geographic Explorer';
  if (summaryType === 'competitive') return 'Competitive Explorer';
  if (summaryType === 'projections') return 'Projections Explorer';
  return 'Explorer';
};

// Reusable section renderer component
export const SectionRenderer = ({ section, sx = {}, showExplorerLink = false, selectedSchool = null, summaryType = null }) => {
  const navigate = useNavigate();
  
  if (!section) return null;
  
  const config = SECTION_CONFIG[section.title] || { icon: <SummarizeIcon /> };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 2,
        backgroundColor: THEME_BGCOLOR,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: THEME_COLOR
        },
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
        <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
          {config.icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
          {section.title}
        </Typography>
      </Box>

      {/* Render table if type is table */}
      {section.type === 'table' && section.competitors ? (
        section.competitors.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>School</strong></TableCell>
                  <TableCell><strong>Enrollment</strong></TableCell>
                  <TableCell><strong>Market Share</strong></TableCell>
                  <TableCell><strong>Academic Ratings</strong></TableCell>
                  <TableCell><strong>School Model</strong></TableCell>
                  <TableCell><strong>Strategic Context</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.competitors.map((competitor, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell><strong>{competitor.name}</strong></TableCell>
                    <TableCell>{competitor.enrollment}</TableCell>
                    <TableCell>{competitor.market_share}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {competitor.academic_ratings || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {competitor.school_model || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      <ReactMarkdown>{competitor.additional_info}</ReactMarkdown>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ ml: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
            No competing schools meeting the mandatory criteria (100+ enrollment, overlapping grades, within drive time) were identified in this area.
          </Typography>
        )
      ) : (
        <Box sx={{ 
          ml: 0.5,
          '& p': { mb: 1.5, lineHeight: 1.7 },
          '& p:last-child': { mb: 0 },
          '& strong': { fontWeight: 600, color: 'text.primary' },
          '& ul': { pl: 3, mb: 1.5 },
          '& ul:last-child': { mb: 0 },
          '& li': { mb: 0.75, lineHeight: 1.6 },
          '& li:last-child': { mb: 0 }
        }}>
          <ReactMarkdown>{section.content || ''}</ReactMarkdown>
        </Box>
      )}
      
      {/* Explorer Link Button */}
      {showExplorerLink && selectedSchool?.uuid && summaryType && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={() => navigate(getExplorerLink(summaryType, selectedSchool.uuid))}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              borderColor: THEME_COLOR,
              color: THEME_COLOR,
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: `${THEME_COLOR}10`
              }
            }}
          >
            Explore in {getExplorerName(summaryType)}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

const FormattedNarrative = ({ 
  narrative, 
  parsedSections, 
  excludeSections = [], 
  includeSections = [], 
  hideExecutiveSummary = false,
  showExplorerLinks = false,
  selectedSchool = null,
  summaryType = null
}) => {
  const navigate = useNavigate();
  
  if (!narrative && !parsedSections) return null;

  // If we have parsed sections (structured data), use those
  if (parsedSections && parsedSections.sections) {
    return (
      <Box>
        {/* Executive Summary */}
        {parsedSections.executive_summary && !hideExecutiveSummary && (includeSections.length === 0 || includeSections.includes('Executive Summary')) && !excludeSections.includes('Executive Summary') && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 2,
              backgroundColor: THEME_BGCOLOR,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                backgroundColor: THEME_COLOR
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
              <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
                <SummarizeIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
                Executive Summary
              </Typography>
            </Box>
            <Box sx={{
              ml: 0.5,
              '& p': { mb: 1.5, lineHeight: 1.7 },
              '& p:last-child': { mb: 0 },
              '& strong': { fontWeight: 600, color: 'text.primary' }
            }}>
              <ReactMarkdown>{parsedSections.executive_summary}</ReactMarkdown>
            </Box>
          </Paper>
        )}

        {/* Sections */}
        {parsedSections.sections
          .filter(section => {
            // parse_summary_sections stores the executive summary both as a
            // top-level field and as a section titled "Executive Summary".
            // Skip the section copy when the dedicated card above is visible.
            const isExecSection = (section.title || '').trim().toLowerCase() === 'executive summary';
            const showingDedicatedExec = Boolean(parsedSections.executive_summary) && !hideExecutiveSummary
              && (includeSections.length === 0 || includeSections.includes('Executive Summary'))
              && !excludeSections.includes('Executive Summary');
            if (isExecSection && showingDedicatedExec) {
              return false;
            }
            // If includeSections is specified, only include those sections
            if (includeSections.length > 0) {
              return includeSections.includes(section.title);
            }
            // Otherwise, exclude sections in excludeSections
            return !excludeSections.includes(section.title);
          })
          .map((section, index) => {
            const config = SECTION_CONFIG[section.title] || { icon: <SummarizeIcon /> };

            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 3,
                  mb: 2,
                  backgroundColor: THEME_BGCOLOR,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: THEME_COLOR
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
                  <Box sx={{ color: THEME_COLOR, display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
                    {config.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
                    {section.title}
                  </Typography>
                </Box>

                {/* Render table if type is table */}
                {section.type === 'table' && section.competitors ? (
                  section.competitors.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>School</strong></TableCell>
                            <TableCell><strong>Enrollment</strong></TableCell>
                            <TableCell><strong>Market Share</strong></TableCell>
                            <TableCell><strong>Academic Ratings</strong></TableCell>
                            <TableCell><strong>School Model</strong></TableCell>
                            <TableCell><strong>Strategic Context</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.competitors.map((competitor, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell><strong>{competitor.name}</strong></TableCell>
                              <TableCell>{competitor.enrollment}</TableCell>
                              <TableCell>{competitor.market_share}</TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {competitor.academic_ratings || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {competitor.school_model || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                <ReactMarkdown>{competitor.additional_info}</ReactMarkdown>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography sx={{ ml: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                      No competing schools meeting the mandatory criteria (100+ enrollment, overlapping grades, within drive time) were identified in this area.
                    </Typography>
                  )
                ) : (
                  <Box sx={{ 
                    ml: 0.5,
                    '& p': { mb: 1.5, lineHeight: 1.7 },
                    '& p:last-child': { mb: 0 },
                    '& strong': { fontWeight: 600, color: 'text.primary' },
                    '& ul': { pl: 3, mb: 1.5 },
                    '& ul:last-child': { mb: 0 },
                    '& li': { mb: 0.75, lineHeight: 1.6 },
                    '& li:last-child': { mb: 0 }
                  }}>
                    <ReactMarkdown>{section.content || ''}</ReactMarkdown>
                  </Box>
                )}
                
                {/* Explorer Link Button */}
                {showExplorerLinks && selectedSchool?.uuid && summaryType && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => navigate(getExplorerLink(summaryType, selectedSchool.uuid))}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        borderColor: THEME_COLOR,
                        color: THEME_COLOR,
                        '&:hover': {
                          borderColor: THEME_COLOR,
                          backgroundColor: `${THEME_COLOR}10`
                        }
                      }}
                    >
                      Explore in {getExplorerName(summaryType)}
                    </Button>
                  </Box>
                )}
              </Paper>
            );
          })}
      </Box>
    );
  }

  // Parse the markdown to extract sections
  const parseNarrative = (text) => {
    const sections = [];
    
    // Split by ## headers (main sections)
    const parts = text.split(/^## /gm).filter(Boolean);
    
    parts.forEach(part => {
      // Get section title and content
      const lines = part.split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();
      
      sections.push({ title, content });
    });
    
    return sections;
  };

  const sections = parseNarrative(narrative);

  // If no sections found (e.g., old format), render as plain markdown
  if (sections.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc' }}>
        <Box sx={{ 
          '& p': { mb: 2 },
          '& strong': { fontWeight: 600 },
          '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 2 },
          '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1.5 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1 }
        }}>
          <ReactMarkdown>{narrative}</ReactMarkdown>
        </Box>
      </Paper>
    );
  }

  return (
    <Box>
      {sections
        .filter(section => {
          // If includeSections is specified, only include those sections
          if (includeSections.length > 0) {
            return includeSections.includes(section.title);
          }
          // Otherwise, exclude sections in excludeSections
          return !excludeSections.includes(section.title);
        })
        .map((section, index) => {
          const config = SECTION_CONFIG[section.title] || { icon: <SummarizeIcon /> };

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                backgroundColor: THEME_BGCOLOR,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: THEME_COLOR
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
                <Box sx={{ 
                  color: THEME_COLOR, 
                  display: 'flex', 
                  alignItems: 'center',
                  '& svg': { fontSize: 28 }
                }}>
                  {config.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
                  {section.title}
                </Typography>
              </Box>
              <Box sx={{ 
                ml: 0.5,
                '& p': { mb: 1.5, lineHeight: 1.7 },
                '& p:last-child': { mb: 0 },
                '& strong': { fontWeight: 600, color: 'text.primary' },
                '& ul': { pl: 3, mb: 1.5 },
                '& ul:last-child': { mb: 0 },
                '& li': { mb: 0.75, lineHeight: 1.6 },
                '& li:last-child': { mb: 0 }
              }}>
                <ReactMarkdown>{section.content}</ReactMarkdown>
              </Box>
              
              {/* Explorer Link Button */}
              {showExplorerLinks && selectedSchool?.uuid && summaryType && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate(getExplorerLink(summaryType, selectedSchool.uuid))}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      borderColor: THEME_COLOR,
                      color: THEME_COLOR,
                      '&:hover': {
                        borderColor: THEME_COLOR,
                        backgroundColor: `${THEME_COLOR}10`
                      }
                    }}
                  >
                    Explore in {getExplorerName(summaryType)}
                  </Button>
                </Box>
              )}
            </Paper>
          );
        })}
    </Box>
  );
};

export default FormattedNarrative;

